import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { EraserIcon, HistoryIcon, RocketIcon, ShieldCheckIcon } from 'lucide-react';
import { showsIssues, type PreflightStatus } from '../lib/preflight';
import { type PublishGate } from '../lib/publish-gate';
import { useTranslation } from 'react-i18next';

export interface PublishActionsProps {
  gate: PublishGate;
  status: PreflightStatus;
  isPublishing: boolean;
  onRunPreflight: () => void;
  /** Discards the findings so the next run starts clean. */
  onClearPreflight: () => void;
  isClearing: boolean;
  onOpenHistory: () => void;
  /**
   * True while the platform half of pre-flight is still out. It runs on a worker
   * after our own rules pass, so the button stays busy past the first response.
   */
  isDryRunning: boolean;
  /** Creatives of this job still uploading, which pre-flight would report as missing. */
  creativesUploading: number;
  onPublish: () => void;
}

/**
 * The gate for a Job: pre-flight first, publish second.
 *
 * Publishing is one action for the whole plan because that is how the work
 * arrives — one spreadsheet, one review, one push — and it stays locked until a
 * check that reflects the CURRENT sheet has come back clean. Editing after a
 * pass reopens the gate rather than quietly letting a stale green through.
 */
export function PublishActions({
  gate,
  status,
  isPublishing,
  onRunPreflight,
  onClearPreflight,
  isClearing,
  onOpenHistory,
  isDryRunning,
  creativesUploading,
  onPublish,
}: PublishActionsProps) {
  const { t } = useTranslation('projects');
  const publishDisabled = !gate.passed || isPublishing || isDryRunning;

  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        variant="ghost"
        size="default"
        onClick={onOpenHistory}
        data-testid="job-history-button"
      >
        <HistoryIcon className="size-4" />
        {t('deliveryJobReview.publishActions.history')}
      </Button>

      {/* Only offered once there is something to clear. */}
      {showsIssues(status) && (
        <Button
          variant="outline"
          size="default"
          onClick={onClearPreflight}
          disabled={isClearing}
          data-testid="job-preflight-clear-button"
        >
          <EraserIcon className="size-4" />
          {isClearing
            ? t('deliveryJobReview.publishActions.clearing')
            : t('deliveryJobReview.publishActions.clearFindings')}
        </Button>
      )}

      {/* One action for both halves: our rules, then the platform's own answer
          on whatever passes them. Splitting them made the platform check an
          optional extra step that was easy to skip before publishing. */}
      <Button
        variant={gate.passed ? 'outline' : 'default'}
        size="default"
        onClick={onRunPreflight}
        disabled={status === 'running' || isDryRunning || creativesUploading > 0}
        data-testid="job-preflight-button"
      >
        <ShieldCheckIcon className="size-4" />
        {status === 'running'
          ? t('deliveryJobReview.publishActions.checking')
          : isDryRunning
            ? t('deliveryJobReview.publishActions.checkingWithThePlatform')
            : creativesUploading > 0
              ? t('deliveryJobReview.publishActions.uploadingCreatives', {
                  count: creativesUploading,
                })
              : showsIssues(status)
                ? t('deliveryJobReview.publishActions.reRunPreflight')
                : t('deliveryJobReview.publishActions.runPreflight')}
      </Button>

      <Tooltip>
        {/* The span carries the hover: a disabled button fires no pointer events, so
            without it the reason for the disabled state is exactly what you cannot read. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant={gate.passed ? 'default' : 'outline'}
              size="default"
              onClick={onPublish}
              disabled={publishDisabled}
              data-testid="job-publish-button"
            >
              <RocketIcon className="size-4" />
              {isPublishing
                ? t('deliveryJobReview.publishActions.publishing')
                : t('deliveryJobReview.publishActions.publish')}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[20rem]">{gate.detail}</TooltipContent>
      </Tooltip>
    </div>
  );
}
